import type {
  CancelRestockWaitlistDTO,
  CreateRestockWaitlistDTO,
  RestockWaitlistListQueryDTO,
  UpdateRestockWaitlistDTO,
} from "@/modules/restock-waitlist/domain/contracts";
import type { RestockWaitlistImportHintDTO } from "@/modules/restock-waitlist/domain/types";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { restockWaitlistApi } from "./client";

const keys = {
  all: ["restock-waitlist"] as const,
  counts: () => [...keys.all, "counts"] as const,
  list: (query?: RestockWaitlistListQueryDTO) =>
    [...keys.all, "list", query] as const,
  detail: (id: string) => [...keys.all, "detail", id] as const,
  importHint: (variantId: string) =>
    [...keys.all, "import-hint", variantId] as const,
  notifyMessage: (id: string) => [...keys.all, "notify-message", id] as const,
};

function invalidateWaitlist(
  qc: ReturnType<typeof useQueryClient>,
  id?: string,
) {
  qc.invalidateQueries({ queryKey: keys.all });
  if (id) qc.invalidateQueries({ queryKey: keys.detail(id) });
}

export function useRestockWaitlistCountsQuery(enabled = true) {
  return useQuery({
    queryKey: keys.counts(),
    queryFn: () => restockWaitlistApi.counts(),
    enabled,
  });
}

export function useRestockWaitlistQuery(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => restockWaitlistApi.get(id),
    enabled: Boolean(id),
  });
}

export function useRestockWaitlistListQuery(
  query?: RestockWaitlistListQueryDTO,
  enabled = true,
) {
  return useQuery({
    queryKey: keys.list(query),
    queryFn: () => restockWaitlistApi.list(query),
    enabled,
  });
}

export function useRestockWaitlistImportHintQuery(variantId: string) {
  return useQuery({
    queryKey: keys.importHint(variantId),
    queryFn: () => restockWaitlistApi.previewImport(variantId),
    enabled: Boolean(variantId),
  });
}

export function useRestockWaitlistImportHintsQuery(variantIds: string[]) {
  const uniqueIds = useMemo(
    () => [...new Set(variantIds.filter(Boolean))],
    [variantIds],
  );

  const queries = useQueries({
    queries: uniqueIds.map((variantId) => ({
      queryKey: keys.importHint(variantId),
      queryFn: () => restockWaitlistApi.previewImport(variantId),
      enabled: Boolean(variantId),
    })),
  });

  const hintsByVariant = useMemo(() => {
    const map: Record<string, RestockWaitlistImportHintDTO> = {};
    uniqueIds.forEach((variantId, index) => {
      const data = queries[index]?.data;
      if (data) map[variantId] = data;
    });
    return map;
  }, [uniqueIds, queries]);

  const isLoading = queries.some((query) => query.isLoading);

  return { hintsByVariant, isLoading };
}

export function useCreateRestockWaitlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRestockWaitlistDTO) =>
      restockWaitlistApi.create(input),
    onSuccess: () => invalidateWaitlist(qc),
  });
}

export function useUpdateRestockWaitlist(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateRestockWaitlistDTO) =>
      restockWaitlistApi.update(id, input),
    onSuccess: () => invalidateWaitlist(qc, id),
  });
}

export function useNotifyRestockWaitlist(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => restockWaitlistApi.notify(id),
    onSuccess: () => invalidateWaitlist(qc, id),
  });
}

export function useNotifyRestockWaitlistMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => restockWaitlistApi.notify(id),
    onSuccess: (_, id) => invalidateWaitlist(qc, id),
  });
}

export function useCancelRestockWaitlist(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input?: CancelRestockWaitlistDTO) =>
      restockWaitlistApi.cancel(id, input),
    onSuccess: () => invalidateWaitlist(qc, id),
  });
}

export async function fetchRestockWaitlistNotifyMessage(id: string) {
  return restockWaitlistApi.getNotifyMessage(id);
}

export { keys as restockWaitlistQueryKeys };
